import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import Select, { OnChangeValue, SelectInstance, Theme, components } from "react-select";

import styled from "styled-components";

import { OptionType } from "../../config/options";
import { resetTrait, setTrait } from "../../state/uwuSlice";
import heartIcon from "../../assets/ui/heart.svg";

const StyledOptionCategory = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-transform: capitalize;
`;

const StyledSelect = styled(Select)<any>`
  * {
    font-size: 1.7rem;
    font-weight: 700;
    letter-spacing: 0.8px;
  }
`;

const HeartIcon = styled.img`
  width: 1.8em;
  height: 1.8em;
  margin-right: 0.8em;
  vertical-align: middle;
  display: inline-block;
`;

const OptionText = styled.span`
  font-size: 1.5rem !important;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 700 !important;
  color: #333 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
`;

interface Props {
  allowAll: boolean;
  name: string;
  ogTraits: Record<string, string> | null;
  options: OptionType[];
  tokensOwned: Record<string, boolean>;
  tokensOwned1155: Record<string, Record<string, boolean>>;
  traits: Record<string, string>;
  fullbody: boolean | null;
  uwuId: number | undefined;
}

// Helper to determine if an option is "special" (not a normal/free option)
const isSpecialOption = (option: OptionType): boolean => {
  return (
    !!option.tokenRestriction ||
    !!option.traitRestriction ||
    !!option.tokenRestriction1155
  );
};

const OptionCategory = ({
  allowAll,
  name,
  ogTraits,
  options,
  tokensOwned,
  tokensOwned1155,
  traits,
  fullbody,
  uwuId,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const [filteredOptions, setFilteredOptions] = useState<OptionType[]>([]);
  const selectInputRef = useRef<SelectInstance>();

  useEffect(() => {
    setFilteredOptions(
      options.filter((value: OptionType) => {
        if (allowAll) {
          return true;
        }

        if (value.testOnly) {
          return false;
        }

        const normalTrait =
          !value.tokenRestriction &&
          !value.traitRestriction &&
          !value.tokenRestriction1155;
        if (normalTrait) return true;

        // Token Restriction
        let ownsToken = false;
        if (value.tokenRestriction) {
          if (tokensOwned[value.tokenRestriction]) ownsToken = true;
        }

        // Trait Restriction
        let ownsTrait = false;
        if (value.traitRestriction) {
          if (
            ogTraits &&
            ogTraits[value.traitRestriction.trait] ===
              value.traitRestriction.value
          )
            ownsTrait = true;
        }

        // ERC1155 Token Restriction
        let owns1155 = false;
        const restriction = value.tokenRestriction1155;
        if (
          restriction &&
          tokensOwned1155 &&
          tokensOwned1155[restriction.token]
        ) {
          owns1155 = restriction.ids.some(
            (id) => tokensOwned1155[restriction.token][id]
          );
        }

        if (ownsToken || ownsTrait || owns1155) return true;

        return false;
      })
    );
    selectInputRef?.current?.clearValue();
  }, [uwuId]);

  const onChange = (value: OnChangeValue<OptionType, false>) => {
    if (value == null) return dispatch(resetTrait(name));

    dispatch(
      setTrait({
        trait: name,
        value: value.name,
        fullbody: value.fullbody ? value.fullbody : false,
      })
    );
  };

  // Custom Option component to show heart for special options (on the left)
  const CustomOption = (props: any) => {
    const { data } = props;
    return (
      <components.Option {...props}>
        <OptionText>
          {isSpecialOption(data) && (
            <HeartIcon src={heartIcon} alt="Special" title="Special option" />
          )}
          {data.name}
        </OptionText>
      </components.Option>
    );
  };

  // Custom SingleValue component to show heart in the select box if selected option is special (on the left)
  const CustomSingleValue = (props: any) => {
    const { data } = props;
    return (
      <components.SingleValue {...props}>
        <OptionText>
          {isSpecialOption(data) && (
            <HeartIcon src={heartIcon} alt="Special" title="Special option" />
          )}
          {data.name}
        </OptionText>
      </components.SingleValue>
    );
  };

  const getOption = (option: OptionType) => option.name;

  return (
    <StyledOptionCategory>
      <Header>{name}</Header>
      <StyledSelect
        ref={selectInputRef}
        options={filteredOptions}
        getOptionLabel={getOption}
        getOptionValue={getOption}
        isClearable
        isDisabled={
          (name === "left accessory" || name === "right accessory") && fullbody
        }
        isSearchable={false}
        onChange={onChange}
        menuPortalTarget={document.body}
        components={{
          Option: CustomOption,
          SingleValue: CustomSingleValue,
        }}
        styles={{
          control: (baseStyles: any, state: any) => ({
            ...baseStyles,
            borderRadius: "12px",
            border: state.menuIsOpen
              ? "2px solid var(--primary)"
              : state.hasValue
              ? "2px solid var(--primary)"
              : "2px solid var(--primary-light)",
            borderBottom: state.menuIsOpen
              ? "4px solid var(--primary)"
              : state.hasValue
              ? "4px solid var(--primary)"
              : "4px solid var(--primary-light)",
            height: "4.8rem",
            boxShadow: "0 0",
            "&:hover": {
              borderColor: "var(--primary)",
            },
          }),
          menu: (baseStyles: any, state: any) => ({
            ...baseStyles,
            textAlign: "center",
            border: "2px solid var(--primary)",
            borderBottom: "4px solid var(--primary)",
            zIndex: 9999,
            position: 'absolute',
            fontSize: '1.5rem !important', // Much larger font size to match options
            padding: '0.2rem 0 !important', // More container padding for larger elements
            maxHeight: '500px', // Increased height for much larger elements
            overflowY: 'auto', // Allow scrolling if needed
            marginTop: '-2px', // Move dropdown closer to selector
            borderRadius: '8px', // Match selector border radius
          }),
          menuPortal: (baseStyles: any) => ({
            ...baseStyles,
            zIndex: 9999,
          }),
          option: (baseStyles: any, state: any) => ({
            ...baseStyles,
            fontSize: '1.5rem !important', // Match the OptionText font size
            fontWeight: '700 !important', // Bolder weight
            padding: '0.4rem 0.8rem !important', // Minimal padding - just enough for text
            minHeight: 'auto !important', // Remove fixed minimum height
            height: 'auto !important', // Let height be determined by content
            lineHeight: '1.2 !important', // Tighter line height
            margin: '0 !important', // Remove any default margins
            borderBottom: '1px solid #f0f0f0 !important', // Subtle separator
            display: 'flex !important', // Ensure flex layout for icon and text
            alignItems: 'center !important', // Center align icon and text vertically
            justifyContent: 'center !important', // Center align horizontally
            gap: '0.5rem !important', // Minimal gap between icon and text
            color: '#333 !important', // Ensure text color is dark and visible
            textAlign: "center !important", // Center align text
          }),
          placeholder: (baseStyles: any, state: any) => ({
            ...baseStyles,
            color: "#e5e5e5",
          }),
          indicatorSeparator: (baseStyles: any, state: any) => ({
            ...baseStyles,
            display: "none",
          }),
        }}
        theme={(theme: Theme) => {
          return {
            ...theme,
            borderRadius: 12,
            colors: {
              ...theme.colors,
              primary25: "var(--primary-light)",
              primary: "var(--primary)",
            },
          };
        }}
      />
    </StyledOptionCategory>
  );
};

export default OptionCategory;
